import React, { useRef, useState, useEffect } from 'react';
import {
  Send, Sparkles, Loader, AlertCircle, MapPin, Award, Briefcase, Upload, X,
  ShieldCheck, BrainCircuit, Users as UsersIcon, Search, Info, TrendingUp,
  Scale
} from 'lucide-react';
import { SparklesIcon, UsersIcon as UsersHero, ShieldCheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import PageHeader from './PageHeader';
import './RecommendAdvocate.css';
import './AnalysisLayout.css';

const FYP2_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

// ─── Animated match score donut ───────────────────────────────────────────────
function CircularMatchScore({ score }) {
  const [animated, setAnimated] = useState(0);
  const pct = Math.min(100, Math.max(0, score));
  const circ = 2 * Math.PI * 54;
  const offset = circ - (animated / 100) * circ;
  const color = pct >= 85 ? '#2ECC71' : pct >= 70 ? '#F4C542' : '#E74C3C';

  useEffect(() => {
    const t = setTimeout(() => setAnimated(pct), 120);
    return () => clearTimeout(t);
  }, [pct]);

  return (
    <div className="circular-match-score">
      <svg className="donut-svg" viewBox="0 0 120 120">
        <circle className="donut-bg" cx="60" cy="60" r="54" />
        <circle
          className="donut-progress" cx="60" cy="60" r="54"
          style={{
            strokeDasharray: circ,
            strokeDashoffset: offset,
            stroke: color,
            transition: 'stroke-dashoffset 1.4s ease-out, stroke 0.3s ease',
          }}
        />
      </svg>
      <div className="donut-center">
        <div className="donut-score">{Math.round(animated)}%</div>
        <div className="donut-label">Match</div>
      </div>
    </div>
  );
}

// ─── Typing dots ──────────────────────────────────────────────────────────────
function TypingIndicator() {
  return (
    <div className="typing-indicator">
      <div className="typing-dot" /><div className="typing-dot" /><div className="typing-dot" />
    </div>
  );
}

// ─── Right panel advocate card ────────────────────────────────────────────────
function AdvocatePanel({ adv }) {
  const pct = Math.round((adv.similarity_score || 0) * 100);
  return (
    <div className="advocate-panel-premium">
      <div className="adv-header-row">
        <div className="adv-avatar-circle">
          {adv.name?.charAt(0) || 'A'}
        </div>
        <div className="adv-name-meta">
          <h4 className="adv-name">{adv.name || 'Advocate'}</h4>
          <span className="adv-tag">Verified Expert</span>
        </div>
      </div>

      <div className="adv-stats-grid">
        {adv.location && (
          <div className="adv-stat-item">
            <MapPin size={14} />
            <span>{adv.location}</span>
          </div>
        )}
        {adv.experience_years > 0 && (
          <div className="adv-stat-item">
            <Award size={14} />
            <span>{adv.experience_years} Years</span>
          </div>
        )}
      </div>

      <div className="adv-practice-areas">
        {(Array.isArray(adv.practice_areas)
          ? adv.practice_areas
          : (typeof adv.practice_areas === 'string' ? adv.practice_areas.split(',') : [])
        ).slice(0, 3).map((area, i) => (
          <span key={i} className="practice-tag">{area.trim()}</span>
        ))}
      </div>

      <p className="adv-reason">
        {adv.match_reason || 'Matched based on relevant legal expertise.'}
      </p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function RecommendAdvocate({ onNavigate }) {
  const [caseText, setCaseText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Only PDF files are supported');
        return;
      }
      setUploadedFile(file);
      setError(null);
    }
  };

  const clearFileUpload = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    const text = caseText.trim();
    const hasFile = uploadedFile !== null;
    const hasText = text.length > 0;

    if ((!hasFile && !hasText) || isLoading) return;

    // Prepare message for display
    let displayText = '';
    if (hasFile && hasText) {
      displayText = `📄 Uploaded: ${uploadedFile.name}\n💬 Context: ${text}`;
    } else if (hasFile) {
      displayText = `📄 Uploaded: ${uploadedFile.name}`;
    } else {
      displayText = text;
    }

    setMessages(prev => [...prev, { role: 'user', text: displayText, ts: new Date().toISOString() }]);
    setCaseText('');
    setUploadedFile(null);
    setIsLoading(true);
    setError(null);

    try {
      let res;

      if (hasFile) {
        // File upload mode - use multipart/form-data
        const formData = new FormData();
        formData.append('file', uploadedFile);
        if (hasText) {
          formData.append('prompt', text);
        }
        formData.append('top_k', 5);

        res = await fetch(`${FYP2_BASE}/api/v1/recommend`, {
          method: 'POST',
          body: formData,
        });
      } else {
        // Text mode - use JSON
        res = await fetch(`${FYP2_BASE}/api/v1/recommend`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ case_text: text, top_k: 5 }),
        });
      }

      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `Server error ${res.status}`);
      }

      const data = await res.json();
      const recs = data.recommendations || [];

      setRecommendations(recs);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: recs.length > 0
          ? `I found ${recs.length} advocates well-matched to your case. Top results are shown on the right.`
          : 'No matching advocates found. Try describing your case in more detail.',
        recs: recs.slice(0, 3),
        ts: new Date().toISOString(),
      }]);
    } catch (err) {
      setError(err.message);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: `Sorry, I couldn't get recommendations right now: ${err.message}`,
        ts: new Date().toISOString(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="analysis-page-container recommend-advocate-page">
      <div className="analysis-hero">
        <div className="hero-text-content">
          <h1>Advocate Matching</h1>
          <p>AI-driven matching to find the perfect legal expert for your case.</p>
        </div>
        <div className="hero-abstract-decoration">
          <div className="abstract-circle abstract-circle-1" style={{ background: '#f0f9ff' }}></div>
          <div className="abstract-circle abstract-circle-2" style={{ background: '#e0f2fe' }}></div>
          <Scale className="hero-bg-icon" size={160} />
        </div>
      </div>

      <div className="analysis-grid">
        {/* Main Column: Chat Interface */}
        <div className="analysis-main-col">
          <div className="chat-section-premium" data-tour="Chatbot Section (Chat window or 'Ask Legal' card)">
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="matching-hero-centered">
                  <div className="matching-hero-icon">
                    <BrainCircuit size={48} />
                  </div>
                  <h2>Describe Your Situation</h2>
                  <p>Tell our AI about your legal matter. We'll analyze the details and cross-reference our database to find advocates with the most relevant expertise.</p>
                  <div className="matching-suggestions">
                    {[
                      'I need help with a corporate contract dispute',
                      'My employer fired me without notice',
                      'I need IP protection for my software',
                    ].map(s => (
                      <button key={s} onClick={() => setCaseText(s)} style={{
                        padding: '8px 14px', borderRadius: 20, border: '1px solid #3D6AFF22',
                        background: '#3D6AFF0f', color: '#3D6AFF', fontSize: 12,
                        cursor: 'pointer', fontWeight: 500,
                      }}>{s}</button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} className={`message ${msg.role}`}>
                    <div className="message-avatar">
                      {msg.role === 'user' ? '👤' : <Sparkles size={18} />}
                    </div>
                    <div className="message-content">
                      <p className="message-text">{msg.text}</p>
                      {/* Inline mini-cards for top 3 */}
                      {msg.recs?.length > 0 && (
                        <div className="embedded-recommendations">
                          {msg.recs.map((adv, ri) => (
                            <div key={ri} className="chat-advocate-card">
                              <div className="adv-avatar-circle mini">
                                {adv.name?.charAt(0) || 'A'}
                              </div>
                              <div className="chat-advocate-details">
                                <h4 className="chat-advocate-name">{adv.name}</h4>
                                <ul className="chat-advocate-bullets">
                                  {(Array.isArray(adv.practice_areas)
                                    ? adv.practice_areas
                                    : (typeof adv.practice_areas === 'string' ? adv.practice_areas.split(',') : [])
                                  ).slice(0, 1).map((a, ai) => <li key={ai}>{a.trim()}</li>)}
                                  {adv.location && <li>{adv.location}</li>}
                                </ul>
                              </div>
                              <div className="chat-match-badge">
                                {Math.round((adv.similarity_score || 0) * 100)}% Match
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <span className="message-time">
                        {new Date(msg.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))
              )}

              {isLoading && (
                <div className="message assistant">
                  <div className="message-avatar"><Sparkles size={18} /></div>
                  <div className="message-content"><TypingIndicator /></div>
                </div>
              )}

              {error && !isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#E74C3C', fontSize: 13, padding: '8px 16px' }}>
                  <AlertCircle size={16} />{error}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

          </div>

          {/* Input bar */}
          <div className="chat-input-bar-premium">
            {uploadedFile && (
              <div className="file-upload-display">
                <div className="file-info">
                  <Upload size={16} />
                  <span>{uploadedFile.name}</span>
                </div>
                <button className="clear-file-btn" onClick={clearFileUpload} title="Remove file">
                  <X size={16} />
                </button>
              </div>
            )}
            <div className="input-controls">
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf" style={{ display: 'none' }} />
              <button className="upload-file-btn" onClick={() => fileInputRef.current?.click()} disabled={isLoading} title="Upload PDF">
                <Upload size={18} />
              </button>
              <input
                className="chat-input"
                placeholder={uploadedFile ? "Add context or notes..." : "Describe your legal case in detail…"}
                value={caseText}
                onChange={e => setCaseText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                disabled={isLoading}
              />
              <button className="send-btn" onClick={handleSend} disabled={isLoading || (!caseText.trim() && !uploadedFile)}>
                {isLoading ? <Loader size={18} className="btn-spinner" /> : <Send size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar Column */}
        <div className="analysis-sidebar-col">
          {recommendations.length > 0 ? (
            <div className="sidebar-info-card top-match-card-premium">
              <div className="card-icon-header"><TrendingUp size={20} /></div>
              <h4>Top Match</h4>
              <AdvocatePanel adv={recommendations[0]} />
              <div className="feature-list" style={{ marginTop: 16 }}>
                <div className="analysis-feature-item">
                  <span>Match Strength</span>
                  <span className="status-label high">{Math.round((recommendations[0].similarity_score || 0) * 100)}%</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="sidebar-info-card">
              <div className="card-icon-header"><Search size={20} /></div>
              <h4>How it works</h4>
              <p>Describe your case, and our AI will perform a semantic analysis to find advocates with matching expertise in our database.</p>
              <div className="feature-list">
                <div className="analysis-feature-item">
                  <span>Search Mode</span>
                  <span className="status-label">RAG + Semantic</span>
                </div>
                <div className="analysis-feature-item">
                  <span>Database</span>
                  <span className="status-label">Global Registry</span>
                </div>
              </div>
            </div>
          )}

          {recommendations.length > 1 && (
            <div className="sidebar-info-card">
              <div className="card-icon-header"><UsersHero className="w-5 h-5" /></div>
              <h4>Other Matches</h4>
              <div className="mini-matches-list">
                {recommendations.slice(1, 4).map((adv, i) => (
                  <div key={i} className="analysis-feature-item" style={{ marginBottom: '8px' }}>
                    <span style={{ fontWeight: 600 }}>{adv.name}</span>
                    <span className="status-label">{Math.round((adv.similarity_score || 0) * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
